import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Briefcase, Loader2, X, Check } from 'lucide-react';
import { searchOccupationsEsco, selectOccupation, getOccupationById } from '../services/occupationsService';

/**
 * OccupationAutocomplete - ESCO occupation search with autosuggest
 *
 * @param {Object} props
 * @param {string} props.label - Label for the input
 * @param {number} props.value - Selected occupation ID (from peoples.occupationId)
 * @param {Object} props.selectedOccupation - Pre-loaded occupation object
 * @param {Function} props.onChange - Callback when occupation is selected (occupationId, occupation)
 * @param {string} props.language - Search language (default: 'fr')
 * @param {string} props.placeholder - Input placeholder
 * @param {string} props.className - Additional CSS classes
 */
const OccupationAutocomplete = ({
    label = 'Profession / Métier',
    value,
    selectedOccupation: initialOccupation,
    onChange,
    language = 'fr',
    placeholder = 'Recherchez votre métier...',
    className = ''
}) => {
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedOccupation, setSelectedOccupation] = useState(initialOccupation || null);
    const [isSaving, setIsSaving] = useState(false);
    const [savingOccupation, setSavingOccupation] = useState(null);

    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const debounceRef = useRef(null);

    // Load occupation if value is provided but no initialOccupation
    useEffect(() => {
        if (value && !initialOccupation) {
            loadOccupation(value);
        }
    }, [value]);

    // Update selected occupation when initialOccupation changes
    useEffect(() => {
        if (initialOccupation) {
            setSelectedOccupation(initialOccupation);
            setInputValue('');
        }
    }, [initialOccupation]);

    const loadOccupation = async (occupationId) => {
        const occupation = await getOccupationById(occupationId);
        if (occupation) {
            setSelectedOccupation(occupation);
        }
    };

    // Debounced search
    const handleSearch = useCallback(async (query) => {
        if (query.length < 2) {
            setSuggestions([]);
            setShowDropdown(false);
            return;
        }

        setIsLoading(true);
        const results = await searchOccupationsEsco(query, language, 10);
        setSuggestions(results);
        setShowDropdown(results.length > 0);
        setIsLoading(false);
    }, [language]);

    // Handle input change with debounce
    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);

        // Clear previous debounce
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        // Debounce search
        debounceRef.current = setTimeout(() => {
            handleSearch(value);
        }, 300);
    };

    // Handle occupation selection
    const handleSelect = async (occupation) => {
        setIsSaving(true);
        setSavingOccupation(occupation);
        setShowDropdown(false);
        setInputValue('');

        try {
            // Save occupation to database (or get existing)
            const savedOccupation = await selectOccupation(occupation);

            if (savedOccupation) {
                setSelectedOccupation({
                    id: savedOccupation.id,
                    escoUri: savedOccupation.escoUri || occupation.uri,
                    [`name_${language}`]: occupation.preferredLabel?.[language] || occupation.title,
                    name_fr: occupation.preferredLabel?.fr || '',
                    name_en: occupation.preferredLabel?.en || occupation.title
                });

                // Notify parent
                if (onChange) {
                    onChange(savedOccupation.id, savedOccupation);
                }
            }
        } catch (error) {
            console.error('Error selecting occupation:', error);
        } finally {
            setIsSaving(false);
            setSavingOccupation(null);
        }
    };

    // Clear selection
    const handleClear = () => {
        setSelectedOccupation(null);
        setInputValue('');
        setSuggestions([]);

        if (onChange) {
            onChange(null, null);
        }
    };

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)
            ) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Get display name based on language
    const getDisplayName = (occupation) => {
        if (!occupation) return '';
        return occupation[`name_${language}`] ||
               occupation.name_fr ||
               occupation.name_en ||
               occupation.title ||
               '';
    };

    return (
        <div className={`relative ${className}`}>
            {label && (
                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    <Briefcase className="w-4 h-4 mr-2 text-cabaza-purple" />
                    {label}
                </label>
            )}

            {/* Saving state */}
            {isSaving && savingOccupation ? (
                <div className="flex items-center gap-2 p-3 bg-purple-50 border border-cabaza-purple/50 rounded-lg">
                    <Loader2 className="w-5 h-5 text-cabaza-purple animate-spin shrink-0" />
                    <span className="flex-1 text-gray-600">
                        Enregistrement : {savingOccupation.preferredLabel?.[language] || savingOccupation.title}...
                    </span>
                </div>
            ) : selectedOccupation ? (
                /* Selected occupation display */
                <div className="flex items-center gap-2 p-3 bg-purple-50 border border-cabaza-purple rounded-lg">
                    <Check className="w-5 h-5 text-green-500 shrink-0" />
                    <span className="flex-1 font-medium text-gray-900">
                        {getDisplayName(selectedOccupation)}
                    </span>
                    <button
                        type="button"
                        onClick={handleClear}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            ) : (
                /* Search input */
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
                        placeholder={placeholder}
                        className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                        disabled={isSaving}
                    />
                    {(isLoading || isSaving) && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-cabaza-purple animate-spin" />
                    )}
                </div>
            )}

            {/* Suggestions dropdown */}
            {showDropdown && suggestions.length > 0 && (
                <div
                    ref={dropdownRef}
                    className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
                >
                    {suggestions.map((occupation, index) => {
                        const displayName = occupation.preferredLabel?.[language] || occupation.title;
                        const englishName = occupation.preferredLabel?.en || occupation.title;

                        return (
                            <button
                                key={`${occupation.uri}-${index}`}
                                type="button"
                                onClick={() => handleSelect(occupation)}
                                className="w-full px-4 py-3 text-left hover:bg-purple-50 transition-colors border-b border-gray-100 last:border-b-0"
                            >
                                <div className="font-medium text-gray-900">
                                    {displayName}
                                </div>
                                {language !== 'en' && englishName && englishName !== displayName && (
                                    <div className="text-xs text-gray-500 mt-0.5">
                                        {englishName}
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* No results message */}
            {showDropdown && suggestions.length === 0 && inputValue.length >= 2 && !isLoading && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                    Aucun métier trouvé pour "{inputValue}"
                </div>
            )}

            <p className="mt-1 text-xs text-gray-500">
                Source: Classification européenne ESCO
            </p>
        </div>
    );
};

export default OccupationAutocomplete;
